import React, { useCallback, useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch } from "react-redux";
import Grid from "@mui/material/Grid";
import { Box, Card } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import { map, mergeRight } from "ramda";
import { useSearchQuery } from "../common/use-searchQuery";
import { useSearch } from "../searchServices";
import { logOut } from "../../auth/authActions";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DotPagination from "./DotPagination";
import { notBlank } from "../../util/ext";
import DateRangeSelect, {
  calculateDate,
  dateRanges,
} from "../../components/dashboard/DateRangeSelect";
import AddNewCollectionDialog from "../collections/browse/AddNewCollectionDialog";
import Image from "../../components/Image";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    minWidth: 735,
    // height: 385,
    height: 355,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    [theme.breakpoints.up("lg")]: {
      height: 385,
    },
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  h4: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    textTransform: "uppercase",
    fontWeight: "normal",
  },

  grey: {
    color: theme.palette.secondary.contrastText,
  },

  body2: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
  },

  addButton: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
  },

  collectionList: {
    marginTop: 50,
  },

  collectionThumbnailWrapper: {
    width: 100,
    height: "auto",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      width: 120,
    },
    [theme.breakpoints.up("xl")]: {
      width: 150,
    },
  },

  collectionThumbnail: {
    width: 100,
    height: 100,
    borderRadius: 20,
    position: "relative",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
    [theme.breakpoints.up("md")]: {
      width: 120,
      height: 120,
    },
    [theme.breakpoints.up("xl")]: {
      width: 150,
      height: 150,
    },
  },

  collectionItem: {
    width: 100,
    [theme.breakpoints.up("md")]: {
      width: 120,
    },
    [theme.breakpoints.up("xl")]: {
      width: 150,
    },
  },

  progressWrapper: {
    flexGrow: 1,
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    color: theme.palette.primary.main,
  },

  newButton: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },

  forwardIcon: {
    color: theme.palette.primary.main,

    "& :first-child": {
      fontSize: "1.2rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
}));

const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [
    {
      name: "type",
      queryParam: "_type",
      terms: [
        {
          code: "ANTHOLOGY",
        },
        {
          code: "COLLECTION",
        },
      ],
    },
  ],
  orderBy: "recordDate",
  orderDirection: "desc",
  otherParams: [],
  appliedPublicationYearGroups: [],
  page: 0,
  limit: 6,
  noFacets: true,
};

const CollectionsCard = ({ title, range }) => {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const isMd = useMediaQuery(theme => theme.breakpoints.up("md"));
  const isXl = useMediaQuery(theme => theme.breakpoints.up("xl"));

  //-- Handle Search state
  const { state, handleUpdatePage, handleUpdateOtherParams } = useSearchQuery(
    mergeRight(
      initialSearchState,
      notBlank(range)
        ? { otherParams: [{ aa: `${calculateDate(range)}|` }] }
        : {},
    ),
  );

  const searchResults = useSearch(state);

  useEffect(() => {
    console.log("useEffect for searchResults...");
    if (searchResults.isError) {
      //-- Send user to login if the token has expired or not logged in
      if (searchResults.error.status === 401) {
        reduxDispatch(logOut());
      }
    }
  }, [searchResults.isError, searchResults.error, reduxDispatch]);

  const handlePageChange = page => {
    console.log("Handling page change...");
    console.log(page);
    handleUpdatePage(page);
  };

  const handleDateRangeChange = useCallback(
    value => {
      console.log(`Search Date : ${value}`);
      if (notBlank(value)) {
        handleUpdateOtherParams([{ aa: `${value}|` }]);
      } else {
        handleUpdateOtherParams([]);
      }
    },
    [handleUpdateOtherParams],
  );

  const [open, setOpen] = useState(false);
  const handleNewCollectionClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  return (
    <Card classes={{ root: classes.root }}>
      <AddNewCollectionDialog
        open={open}
        onClose={handleDialogClose}
        onSave={handleDialogClose}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Link
            to={{ pathname: "/creator/collections" }}
            state={{ search: state }}
          >
            <Typography variant="h4" component="span">
              {title || "My Collections"}
            </Typography>
          </Link>
        </Grid>

        <Grid item>
          <Grid container alignItems="center" spacing={5}>
            <Grid item>
              <Button
                size="small"
                classes={{
                  textSizeSmall: classes.addButton,
                  iconSizeSmall: classes.addButton,
                }}
                variant="text"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleNewCollectionClick}
              >
                New Collection
              </Button>
            </Grid>
            <Grid item>
              <DateRangeSelect
                initialRange={range || dateRanges.all}
                onDateRangeChange={handleDateRangeChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {searchResults.isSuccess ? (
        <Grid container spacing={4} justifyContent="flex-start">
          <Grid item>
            <Box>
              <Typography
                variant="h1"
                component="div"
                classes={{ h1: classes.h1 }}
              >
                {searchResults.data.totalCount.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Typography
            variant="h1"
            component="span"
            classes={{ h1: classes.h1 }}
          >
            Loading...
          </Typography>
        </Box>
      )}
      {searchResults.isSuccess ? (
        <Box className={classes.collectionList}>
          {notBlank(searchResults.data.media) ? (
            <Grid container justifyContent="center" spacing={isMd ? 5 : 3}>
              {map(
                collection => (
                  <Grid item key={collection.id}>
                    <Link to={`/creator/collections/${collection.id}`}>
                      <Box className={classes.collectionItem}>
                        <Box className={classes.collectionThumbnailWrapper}>
                          <Image
                            className={classes.collectionThumbnail}
                            height={isXl ? 150 : isMd ? 120 : 100}
                            src={collection.thumbnailUrl}
                            alt="thumbnail of collection"
                          />
                        </Box>
                        <Typography
                          color="primary"
                          component="div"
                          variant="body2"
                          noWrap
                          classes={{ body2: classes.body2 }}
                        >
                          {collection.name}
                        </Typography>
                      </Box>
                    </Link>
                  </Grid>
                ),
                searchResults.data.media,
              )}
            </Grid>
          ) : (
            <Box
              width={"100%"}
              height={"100%"}
              ml={"auto"}
              mr={"auto"}
              mt={12}
              textAlign="center"
            >
              <Typography
                component="div"
                variant="body1"
                classes={{ body1: classes.body1 }}
              >
                No collections have been created in the specified date range.
              </Typography>
              <Button
                size="small"
                classes={{
                  textSizeSmall: classes.addButton,
                  iconSizeSmall: classes.addButton,
                }}
                variant="text"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleNewCollectionClick}
              >
                New Collection
              </Button>
            </Box>
          )}

          <Box mt={4}>
            <DotPagination
              totalCount={searchResults.data.totalCount}
              page={state.page}
              onPageSelect={handlePageChange}
            />
          </Box>
        </Box>
      ) : (
        <Box className={classes.progressWrapper}>
          <PanelLoadingIndicator />
        </Box>
      )}
    </Card>
  );
};

export default CollectionsCard;
